import { createFileRoute, redirect } from "@tanstack/react-router";
import NewsPost from "@/pages/NewsPost";
import { routeHead } from "@/lib/seo/route-head";
import { allowedNewsImage, DEFAULT_SITE_IMAGE } from "@/utils/newsImage";
import { supabase } from "@/integrations/supabase/client";
import { segmentByLegacySlug } from "@/lib/segmentStats";

export const Route = createFileRoute("/blog/stiri/$slug/")({
  loader: async ({ params }) => {
    const seg = segmentByLegacySlug(params.slug);
    if (seg) throw redirect({ href: `/statistici/${seg.slug}`, statusCode: 301 });
    const { data } = await supabase
      .from("news_posts")
      .select("slug, title, meta_title, meta_description, lead, category, published_at, created_at, updated_at, og_image_url")
      .eq("slug", params.slug)
      .eq("country", "RO")
      .maybeSingle();
    return { post: data ?? null };
  },
  head: ({ params, loaderData }) => {
    const base = routeHead("/blog/stiri/$slug/", params as Record<string, string>);
    const post = loaderData?.post;
    if (!post) return base;

    const url = `https://roautomarket.ro/blog/stiri/${post.slug}`;
    const description = post.meta_description || post.lead || "";
    const pageTitle = `${post.meta_title || post.title} | RO Auto Market`;
    const image = allowedNewsImage(post.og_image_url) ?? DEFAULT_SITE_IMAGE;
    const keep = (base.meta ?? []).filter(
      (m: any) =>
        m?.name !== "description" &&
        m?.property !== "og:title" &&
        m?.property !== "og:description" &&
        m?.name !== "twitter:title" &&
        m?.name !== "twitter:description" &&
        m?.title === undefined &&
        m?.name !== "robots",
    );
    // Excepție: articolul despre regulile 2026 rămâne indexabil (decizie 06.10.2026);
    // restul știrilor automate rămân noindex, follow.
    const INDEXABLE_STIRI = new Set(["reguli-noi-masini-second-hand-romania-2026"]);
    const robots = INDEXABLE_STIRI.has(post.slug) ? "index, follow" : "noindex, follow";
    return {
      ...base,
      meta: [
        { title: pageTitle },
        ...keep,
        { name: "robots", content: robots },
        { name: "description", content: description },
        { property: "og:title", content: post.meta_title || post.title },
        { property: "og:description", content: description },
        { name: "twitter:title", content: post.meta_title || post.title },
        { name: "twitter:description", content: description },
        { property: "og:image", content: image },
        { name: "twitter:image", content: image },
        { property: "og:type", content: "article" },
        { property: "article:published_time", content: post.published_at || post.created_at || "" },
      ],

      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: post.meta_title || post.title,
            description,
            image,
            datePublished: post.published_at || post.created_at,
            dateModified: post.updated_at || post.published_at || post.created_at,
            articleSection: post.category,
            author: {
              "@type": "Person",
              name: "Redacția RO Auto Market",
              url: "https://roautomarket.ro/despre-noi",
            },
            publisher: {
              "@type": "Organization",
              name: "RO Auto Market",
              logo: {
                "@type": "ImageObject",
                url: "https://roautomarket.ro/og-roautomarket.png",
              },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
          }),
        },
      ],
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <NewsPost />
  );
}
